import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  ExpandableSection,
  Form,
  FormGroup,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Switch,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { Certificate, ZerosslOrder, ZerosslOrderCreateRequest } from '../../../types/models/Certificate';
import type { ApiResponseCertificateZerosslOrderPair } from '../../../types/api/ApiResponseCertificate';
import { ChipListField, looksLikeHostname } from './CreateCertificateModal';
import ZerosslOrderPanel from './ZerosslOrderPanel';

interface CreateZerosslOrderModalProps {
  platformId: number;
  platformName: string;
  onClose: () => void;
  /** The freshly created pending row (order merged in) for the list insert. */
  onCreated: (certificate: Certificate) => void;
  /** Fresh pair from the in-modal polling — patch the list row in place. */
  onPairChange: (certificate: Certificate) => void;
  /** The placed order vanished (deleted from another session) — drop the list row. */
  onOrderMissing?: (certificateId: number) => void;
}

// Accepts a hostname with an optional leading `*.` wildcard label. The
// backend is authoritative; this only catches obvious typos before commit.
function validateDomainEntry(value: string): string | null {
  const trimmed = value.trim().toLowerCase();
  const host = trimmed.startsWith('*.') ? trimmed.slice(2) : trimmed;
  if (!looksLikeHostname(host)) {
    return `${value} is not a valid domain name`;
  }
  return null;
}

/**
 * Order form for ZeroSSL platforms — POST /datacenters/certificates branches
 * on the platform's type, so this replaces the OPNsense CSR form entirely:
 * the Manager generates the keypair + CSR server-side and the user only picks
 * domains, validity, and renewal behaviour.
 *
 * After a successful order the modal swaps to the live ZerosslOrderPanel
 * (validation instructions + 10s polling), so a fully DNS-managed order
 * usually flips to "issued" without the user leaving the modal. Parents mount
 * this component only while open, so every open starts from a fresh state.
 */
function CreateZerosslOrderModal({
  platformId,
  platformName,
  onClose,
  onCreated,
  onPairChange,
  onOrderMissing,
}: CreateZerosslOrderModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [domains, setDomains] = useState<string[]>([]);
  const [validityDays, setValidityDays] = useState<'90' | '365'>('90');
  const [autoRenew, setAutoRenew] = useState<boolean>(true);
  const [keyType, setKeyType] = useState<'rsa2048' | 'rsa4096'>('rsa2048');
  const [isKeyExpanded, setIsKeyExpanded] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [placedPair, setPlacedPair] = useState<{ certificate: Certificate; order: ZerosslOrder } | null>(null);

  const cnTooLong = domains.length > 0 && domains[0].trim().length > 64;
  const tooManyDomains = domains.length > 50;
  const hasWildcard = domains.some((domain) => domain.trim().startsWith('*.'));
  const canSubmit = !isSubmitting && domains.length > 0 && !tooManyDomains && !cnTooLong;

  const notifyFailure = (description: string) => {
    addNotification({
      title: 'Could not place the certificate order',
      srTitle: 'Error alert',
      description,
      variant: 'danger',
    });
    setDrawerOpen(true);
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const body: ZerosslOrderCreateRequest = {
        certificate_platform_id: platformId,
        domains,
        validity_days: validityDays === '365' ? 365 : 90,
        auto_renew: autoRenew,
        key_type: keyType,
      };
      const response = await apiFetch('/datacenters/certificates', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      // Branch on the JSend body, not response.ok: order rejections (quota,
      // plan limits, rejected domains) arrive as `status: 'error'` at HTTP
      // 200 with an upstream-safe message that is meant to be shown verbatim.
      const responseBody = (await response.json().catch(() => null)) as ApiResponseCertificateZerosslOrderPair | null;
      if (responseBody?.status === 'success' && responseBody.data?.certificate && responseBody.data.zerossl_order) {
        const certificate = responseBody.data.certificate;
        const order = responseBody.data.zerossl_order;
        setPlacedPair({ certificate, order });
        onCreated({ ...certificate, zerossl_order: order });
        addNotification({
          title: 'Certificate order placed',
          srTitle: 'Success alert',
          description: `The order for ${certificate.cn} was placed on ${platformName}. It will be issued once the DNS validation completes.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        return;
      }
      const message =
        responseBody?.message ?? `Request failed with status ${response.status} ${response.statusText}`.trim();
      setSubmitError(message);
      notifyFailure(message);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setSubmitError(message);
      notifyFailure(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal variant="medium" isOpen onClose={onClose} aria-label={`Order certificate on ${platformName}`}>
      <ModalHeader title={`Order certificate on ${platformName}`} />
      <ModalBody>
        {placedPair !== null ? (
          <ZerosslOrderPanel
            certificate={placedPair.certificate}
            order={placedPair.order}
            onPairChange={(certificate, order) => onPairChange({ ...certificate, zerossl_order: order })}
            onOrderMissing={() => onOrderMissing?.(placedPair.certificate.id)}
          />
        ) : (
          <>
            {submitError !== null && (
              <Alert variant="danger" isInline title="Could not place the order" className="pf-v6-u-mb-md">
                {submitError}
              </Alert>
            )}
            <Form
              onSubmit={(event) => {
                event.preventDefault();
                void handleSubmit();
              }}
            >
              {/* The wrapper keeps the helper glued to the chip field — as
                  siblings the Form's row gap would split them apart. */}
              <div>
                <ChipListField
                  label="Domains"
                  fieldId="zerossl-order-domains"
                  values={domains}
                  onChange={setDomains}
                  placeholder="Add a domain and press Enter"
                  validateEntry={validateDomainEntry}
                />
                <FormHelperText>
                  <HelperText>
                    {cnTooLong ? (
                      <HelperTextItem variant="error">
                        The first domain becomes the certificate&apos;s common name and must be 64 characters or fewer.
                      </HelperTextItem>
                    ) : tooManyDomains ? (
                      <HelperTextItem variant="error">An order can cover at most 50 domains.</HelperTextItem>
                    ) : (
                      <HelperTextItem>
                        Up to 50 domains — the first becomes the certificate&apos;s common name. Validation records are
                        created automatically for domains hosted on Manager-managed DNS.
                      </HelperTextItem>
                    )}
                    {hasWildcard && (
                      <HelperTextItem variant="warning">
                        Wildcard domains (*.example.com) require a paying ZeroSSL plan.
                      </HelperTextItem>
                    )}
                  </HelperText>
                </FormHelperText>
              </div>

              <FormGroup label="Validity" fieldId="zerossl-order-validity">
                <FormSelect
                  id="zerossl-order-validity"
                  value={validityDays}
                  onChange={(_event, value) => setValidityDays(value as '90' | '365')}
                  aria-label="Certificate validity"
                >
                  <FormSelectOption value="90" label="90 days" />
                  <FormSelectOption value="365" label="365 days — requires a paying ZeroSSL plan" />
                </FormSelect>
              </FormGroup>

              <FormGroup label="Renewal" fieldId="zerossl-order-auto-renew">
                <Switch
                  id="zerossl-order-auto-renew"
                  label="Renew automatically"
                  isChecked={autoRenew}
                  onChange={(_event, checked) => setAutoRenew(checked)}
                  isDisabled={isSubmitting}
                />
                <FormHelperText>
                  <HelperText>
                    <HelperTextItem>
                      Renews automatically about 30 days before expiry when all domains use Manager-managed DNS.
                    </HelperTextItem>
                  </HelperText>
                </FormHelperText>
              </FormGroup>

              <ExpandableSection
                toggleText="Key"
                isExpanded={isKeyExpanded}
                onToggle={(_event, expanded) => setIsKeyExpanded(expanded)}
              >
                <FormGroup label="Key type" fieldId="zerossl-order-key-type">
                  <FormSelect
                    id="zerossl-order-key-type"
                    value={keyType}
                    onChange={(_event, value) => setKeyType(value as 'rsa2048' | 'rsa4096')}
                    aria-label="Key type"
                  >
                    <FormSelectOption value="rsa2048" label="RSA 2048" />
                    <FormSelectOption value="rsa4096" label="RSA 4096" />
                  </FormSelect>
                  <FormHelperText>
                    <HelperText>
                      <HelperTextItem>
                        The key pair is generated and stored by the Manager — ZeroSSL never sees the private key.
                      </HelperTextItem>
                    </HelperText>
                  </FormHelperText>
                </FormGroup>
              </ExpandableSection>
            </Form>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {placedPair !== null ? (
          <>
            <Button
              variant="primary"
              component={(props) => <Link {...props} to={`/datacenter/certificates/${placedPair.certificate.id}`} />}
            >
              View certificate details
            </Button>
            <Button variant="link" onClick={onClose}>
              Close
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="primary"
              onClick={() => void handleSubmit()}
              isLoading={isSubmitting}
              isDisabled={!canSubmit}
            >
              Place order
            </Button>
            <Button variant="link" onClick={onClose} isDisabled={isSubmitting}>
              Cancel
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}

export default CreateZerosslOrderModal;
